import { BrandGlyph, BrandIconChip, hasBrandIcon } from './BrandIcon';
import { DiyMark } from './DiyMark';
import { CCOLOR } from '../data/categories';
import type { CategoryId } from '../features/catalog/model';

export interface PlatformMarkProps {
  /** catalog platform id, e.g. `tiktok`, `web`, `seo` */
  id: string;
  /** the platform's fallback HugeIcons name from the catalog */
  icon: string;
  /** the platform's catalog category, which owns the mark's colour */
  category: CategoryId;
  /** box size in px, or the glyph size when `bare` */
  size?: number;
  /** glyph size in px inside the chip */
  iconSize?: number;
  /** corner radius in px */
  radius?: number;
  /** Draw the mark alone, with no chip box, because the caller already owns the
   *  surface it sits on (a pill, a strip of small marks). */
  bare?: boolean;
}

/**
 * The one rule for how a platform is drawn anywhere on the site: its real brand
 * logo when one exists, and otherwise the hand-drawn pictogram the catalog
 * draws for the platforms that have no logo (a browser turning into braces for
 * web scraping, a ranked results page for SEO). The colour comes from the
 * platform's category, so no caller carries a second palette.
 *
 * Every surface shares it, which is the point: the home page and `/catalog`
 * used to draw the same platform two different ways - a lucide globe or a flat
 * category chip against the catalog's pictogram - and a mark that changes
 * between pages reads as a different product.
 */
export const PlatformMark = ({
  id,
  icon,
  category,
  size = 38,
  iconSize = 19,
  radius = 10,
  bare = false,
}: PlatformMarkProps) => {
  if (bare) {
    return hasBrandIcon(id) ? (
      <BrandGlyph id={id} size={size} />
    ) : (
      <DiyMark id={id} icon={icon} color={CCOLOR[category]} size={size} iconSize={size} bare />
    );
  }
  return hasBrandIcon(id) ? (
    <BrandIconChip id={id} size={size} iconSize={iconSize} radius={radius} className="shrink-0" />
  ) : (
    <DiyMark
      id={id}
      icon={icon}
      color={CCOLOR[category]}
      size={size}
      iconSize={iconSize}
      radius={radius}
    />
  );
};
